/**
 * HTML-based Redline Output Format
 *
 * Describes the LLM-facing output schema and prompt for in-paragraph edits
 * expressed as final HTML with `<change reasoning="...">` wrappers, plus the
 * utilities used to parse that HTML into range-marker-based review ranges
 * consumed by the rest of the redline pipeline.
 */

import type { Element, ElementContent, Properties, Root, RootContent } from 'hast';
import rehypeParse from 'rehype-parse';
import { unified } from 'unified';
import { z } from 'zod';

import { LIST_ATTRIBUTE_NAMES } from '../extract-html/list-attributes';
import type { CastParagraph } from '../extract-html/types';
import { REDLINE_RANGE_END, REDLINE_RANGE_START } from '../llm-range-markers';

// =============================================================================
// Schema
// =============================================================================

/**
 * The discriminant is named `_type`, not `type`, on purpose: the leading
 * underscore sorts before every lowercase key, so `sort-keys` puts it first
 * automatically — no eslint-disable needed.
 *
 * Keeping the discriminant first is load-bearing, not style: the zod property
 * order becomes the JSON Schema property order OpenAI sees, and models tend to
 * generate keys in that order. Under strict structured outputs the union is an
 * `anyOf`, and the constrained decoder eliminates branches as keys stream out.
 * If the discriminant arrived last, a near-miss field set would silently coerce
 * into whichever branch is still viable (e.g. an edit-paragraph with a stray
 * `reasoning` key would be force-completed as `remove-table`, the only branch
 * whose non-discriminant keys are exactly {documentId, id, reasoning}). `_type`
 * first pins the branch before any other key can misroute it; the remaining
 * keys are free to be alphabetical.
 */

/**
 * Which types carry a top-level `reasoning` field, and why:
 *
 * Every suggestion the user sees carries a "why" somewhere, but two types
 * deliberately have no top-level `reasoning`:
 *
 * - `edit-paragraph`: reasoning is per-change, inside the html as
 *   `<change reasoning="...">`. One paragraph can carry several changes,
 *   each needing its own explanation (each becomes its own action, marker
 *   pair, and card row), so a single top-level field would be ambiguous.
 * - `note`: the `text` IS the user-facing explanation — a reasoning field
 *   would just restate it.
 *
 * The other six types (insert, remove, remove-rows, remove-columns,
 * remove-table, insert-rows) each take one `reasoning` shown on the chat
 * card and stored on the suggestion start marker.
 *
 * Caution when changing this: under strict structured outputs the decoder
 * distinguishes union branches by their field sets (see the key-order
 * comment above), so which types have `reasoning` is part of what keeps a
 * near-miss output from morphing into the wrong type. If `edit-paragraph`
 * ever gains a top-level `reasoning` (it would close the last such trap),
 * make it a required one-sentence summary used as the fallback when a
 * `<change>` lacks a reasoning attribute — not a replacement for the
 * per-change reasoning.
 */

export const EditParagraphSuggestionSchema = z.object({
  _type: z.literal('edit-paragraph'),
  documentId: z.string().describe('The id of the document that contains the paragraph'),
  html: z.string().describe('The replacement HTML for the paragraph'),
  id: z.string().describe('The id of the paragraph to edit'),
});

export const InsertSuggestionSchema = z.object({
  _type: z.literal('insert'),
  documentId: z.string().describe('The id of the document to insert into'),
  html: z.string().describe('The HTML content to insert, without <change> markers'),
  id: z.string().describe('The id of the existing paragraph the insertion is relative to'),
  position: z
    .enum(['before', 'after'])
    .describe('Whether to insert the content before or after the paragraph'),
  reasoning: z.string().describe('Why this content is being inserted'),
});

export const RemoveSuggestionSchema = z.object({
  _type: z.literal('remove'),
  documentId: z.string().describe('The id of the document to remove content from'),
  ids: z.array(z.string()).describe('The ids of every paragraph to remove'),
  reasoning: z.string().describe('Why this content is being removed'),
});

export const RemoveRowsSuggestionSchema = z.object({
  _type: z.literal('remove-rows'),
  documentId: z.string().describe('The id of the document to remove table rows from'),
  ids: z
    .array(z.string())
    .describe('The ids of the rows to remove — each a `row-…` id from a `<tr>` element'),
  reasoning: z.string().describe('Why these rows are being removed'),
});

export const RemoveColumnsSuggestionSchema = z.object({
  _type: z.literal('remove-columns'),
  documentId: z.string().describe('The id of the document to remove table columns from'),
  ids: z
    .array(z.string())
    .describe(
      'Paragraph ids identifying the columns to remove — one id inside any cell of each column'
    ),
  reasoning: z.string().describe('Why these columns are being removed'),
});

export const RemoveTableSuggestionSchema = z.object({
  _type: z.literal('remove-table'),
  documentId: z.string().describe('The id of the document to remove the table from'),
  id: z
    .string()
    .describe('The id of the table to remove — a `table-…` id from a `<table>` element'),
  reasoning: z.string().describe('Why the table is being removed'),
});

export const InsertRowsSuggestionSchema = z.object({
  _type: z.literal('insert-rows'),
  documentId: z.string().describe('The id of the document to insert table rows into'),
  html: z.string().describe('One or more <tr> elements with the new rows and their cell content'),
  id: z.string().describe('The `row-…` id of the reference row the insertion is relative to'),
  position: z
    .enum(['above', 'below'])
    .describe('Whether the new rows go immediately above or below the reference row'),
  reasoning: z.string().describe('Why these rows are being inserted'),
});

export const NoteSuggestionSchema = z.object({
  _type: z.literal('note'),
  documentId: z.string().describe('The id of the document the note is about'),
  text: z.string().describe('A short user-facing note explaining why an edit was not made'),
});

/**
 * `suggest` must be a plain `z.union`, not `z.discriminatedUnion`: zod v4
 * serializes discriminated unions to JSON Schema `oneOf`, which OpenAI's
 * structured-output endpoint rejects (`'oneOf' is not permitted`). A plain
 * union serializes to `anyOf`, which is accepted, and the distinct `type`
 * literals keep parsing unambiguous.
 */
export const HtmlEditSchema = z.object({
  suggest: z.union([
    EditParagraphSuggestionSchema,
    InsertSuggestionSchema,
    RemoveSuggestionSchema,
    RemoveRowsSuggestionSchema,
    RemoveColumnsSuggestionSchema,
    RemoveTableSuggestionSchema,
    InsertRowsSuggestionSchema,
    NoteSuggestionSchema,
  ]),
});

export type EditParagraphSuggestion = z.infer<typeof EditParagraphSuggestionSchema>;
export type InsertSuggestion = z.infer<typeof InsertSuggestionSchema>;
export type RemoveSuggestion = z.infer<typeof RemoveSuggestionSchema>;
export type RemoveRowsSuggestion = z.infer<typeof RemoveRowsSuggestionSchema>;
export type RemoveColumnsSuggestion = z.infer<typeof RemoveColumnsSuggestionSchema>;
export type RemoveTableSuggestion = z.infer<typeof RemoveTableSuggestionSchema>;
export type InsertRowsSuggestion = z.infer<typeof InsertRowsSuggestionSchema>;
export type NoteSuggestion = z.infer<typeof NoteSuggestionSchema>;
export type HtmlEdit = z.infer<typeof HtmlEditSchema>;

// =============================================================================
// HTML → HAST
// =============================================================================

const htmlProcessor = unified().use(rehypeParse, { fragment: true });

export function parseHtmlToHast(html: string): Root {
  return htmlProcessor.parse(html) as Root;
}

// =============================================================================
// HAST → ReviewRanges
// =============================================================================

export type ReviewRange = {
  reasoning: string;
  type: 'change';
};

export type ReviewRanges = {
  id: string;
  ranges: ReviewRange[];
  text: string;
};

const DEFAULT_REASONING = 'No reasoning provided';

/**
 * Flattens a paragraph element from the LLM's html into its plain text, with
 * range markers around each `<change>` region.
 *
 * Inline formatting in the html (bold, italics, links, …) is dropped here —
 * the redline pipeline diffs plain text only, so a formatting-only edit
 * applies as a silent no-op. The edit-paragraph prompt forbids the model
 * from emitting such edits for exactly that reason. We intend to support
 * suggested formatting changes later (the document model already has
 * `textFormat` / `paragraphFormat` edit types with accept/reject); that work
 * starts by making this extraction formatting-aware.
 */
export function hastToReviewRanges(element: Element): ReviewRanges {
  const id = extractId(element.properties);
  const ranges: ReviewRange[] = [];
  let text = '';
  let depth = 0;

  function walk(node: ElementContent): void {
    if (node.type === 'text') {
      text += node.value;
      return;
    }

    if (node.type !== 'element') return;

    if (node.tagName === 'change') {
      if (depth > 0) {
        for (const child of node.children) walk(child);
        return;
      }
      ranges.push({ reasoning: extractReasoning(node.properties), type: 'change' });
      text += REDLINE_RANGE_START;
      depth++;
      for (const child of node.children) walk(child);
      depth--;
      text += REDLINE_RANGE_END;
      return;
    }

    for (const child of node.children) walk(child);
  }

  for (const child of element.children) walk(child);

  return { id, ranges, text };
}

export function findFirstParagraphElement(root: Root): Element | null {
  return findParagraph(root.children);
}

function findParagraph(nodes: readonly (RootContent | ElementContent)[]): Element | null {
  for (const node of nodes) {
    if (node.type !== 'element') continue;
    if (node.tagName === 'p') return node;
    const nested = findParagraph(node.children);
    if (nested) return nested;
  }
  return null;
}

function extractId(properties: Properties | undefined): string {
  const raw = properties?.id;
  return typeof raw === 'string' ? raw : '';
}

function extractReasoning(properties: Properties | undefined): string {
  const raw = properties?.reasoning;
  if (typeof raw === 'string' && raw.trim().length > 0) return raw;
  return DEFAULT_REASONING;
}

// =============================================================================
// Per-range content splitting
// =============================================================================

/**
 * Given paragraph text with multiple `⟬…⟭` marker pairs, return a copy that
 * keeps only the `rangeIndex`-th marker pair. The text inside stripped pairs
 * is preserved; only the marker characters are removed.
 *
 * This matches the existing per-action CodeBlockRedlineEdit contract which
 * requires exactly one marker pair per action.
 */
export function extractSingleRangeContent(textWithMarkers: string, rangeIndex: number): string {
  let result = '';
  let currentRange = -1;
  for (const ch of textWithMarkers) {
    if (ch === REDLINE_RANGE_START) {
      currentRange++;
      if (currentRange === rangeIndex) result += REDLINE_RANGE_START;
      continue;
    }
    if (ch === REDLINE_RANGE_END) {
      if (currentRange === rangeIndex) result += REDLINE_RANGE_END;
      continue;
    }
    result += ch;
  }
  return result;
}

// =============================================================================
// Prompt
// =============================================================================

export const HTML_OUTPUT_FORMAT_TITLE = 'HTML Suggestion Output Format';

/**
 * Property-key names used in the prompt prose below, pinned to the schema with
 * `satisfies keyof` so a rename breaks the build here instead of leaving stale
 * prompt text. `keyof HtmlEdit['suggest']` is the set of keys every suggestion
 * shares (the discriminant plus `documentId`). The per-type fields
 * (html/id/position/reasoning) recur across suggestion types, so they are pinned
 * to `InsertSuggestion` as a representative carrier — each type's full field set
 * is already checked by its `*_FIELD_ORDER` / `*_FIELD_DESCRIPTIONS` above.
 */
const suggestKey = 'suggest' satisfies keyof HtmlEdit;
const typeKey = '_type' satisfies keyof HtmlEdit['suggest'];
const htmlKey = 'html' satisfies keyof InsertSuggestion;
const idKey = 'id' satisfies keyof InsertSuggestion;
const positionKey = 'position' satisfies keyof InsertSuggestion;
const reasoningKey = 'reasoning' satisfies keyof InsertSuggestion;

/**
 * Values referenced in the prose are pinned the same way: `position` values to
 * their schema enums, and the list-item vocabulary to the shared
 * `LIST_ATTRIBUTE_NAMES` map the document-context serializer emits — the
 * insert html deliberately uses the same `list-*` attributes the model sees in
 * its input — with the two `list-type` values pinned to `CastParagraph`.
 */
const beforePosition = 'before' satisfies InsertSuggestion['position'];
const afterPosition = 'after' satisfies InsertSuggestion['position'];
const abovePosition = 'above' satisfies InsertRowsSuggestion['position'];
const belowPosition = 'below' satisfies InsertRowsSuggestion['position'];
const listLevelAttr = LIST_ATTRIBUTE_NAMES.listLevel;
const listMarkerAttr = LIST_ATTRIBUTE_NAMES.listMarker;
const listTypeAttr = LIST_ATTRIBUTE_NAMES.listType;
const numberedListType = 'numbered' satisfies NonNullable<CastParagraph['listType']>;
const bulletListType = 'bullet' satisfies NonNullable<CastParagraph['listType']>;

/**
 * The discriminant value of each suggestion type, for the section headers and
 * prose below. Pinned to the schema literals so renaming one breaks the build
 * at these lines instead of leaving stale references in the prompt.
 */
const editParagraphType = 'edit-paragraph' satisfies EditParagraphSuggestion['_type'];
const insertType = 'insert' satisfies InsertSuggestion['_type'];
const removeType = 'remove' satisfies RemoveSuggestion['_type'];
const removeRowsType = 'remove-rows' satisfies RemoveRowsSuggestion['_type'];
const removeColumnsType = 'remove-columns' satisfies RemoveColumnsSuggestion['_type'];
const removeTableType = 'remove-table' satisfies RemoveTableSuggestion['_type'];
const insertRowsType = 'insert-rows' satisfies InsertRowsSuggestion['_type'];
const noteType = 'note' satisfies NoteSuggestion['_type'];

const EDIT_PARAGRAPH_FIELD_DESCRIPTIONS = {
  _type: `\`"${editParagraphType}"\``,
  documentId: 'The id of the document that contains the paragraph being edited',
  html: 'The final desired HTML for the paragraph',
  id: 'The id of the paragraph being edited — must be copied **exactly** from the `id` attribute of the `<p>` element in the document context. Never invent or alter this value.',
} satisfies Record<keyof EditParagraphSuggestion, string>;

const EDIT_PARAGRAPH_FIELD_ORDER = [
  '_type',
  'documentId',
  'html',
  'id',
] satisfies (keyof EditParagraphSuggestion)[];

const INSERT_FIELD_DESCRIPTIONS = {
  _type: `\`"${insertType}"\``,
  documentId: 'The id of the document the new content is inserted into',
  html: 'The HTML for the new content being inserted. Plain HTML only — do NOT include `<change>` elements; the entire insertion is treated as one change.',
  id: 'The id of the existing paragraph the insertion is relative to — must be copied **exactly** from the `id` attribute of the `<p>` element in the document context. Never invent or alter this value.',
  position: `\`"${beforePosition}"\` or \`"${afterPosition}"\` — whether the new content goes immediately before or immediately after the paragraph identified by \`${idKey}\``,
  reasoning: 'Why this content is being added — shown to the user alongside the insertion',
} satisfies Record<keyof InsertSuggestion, string>;

const INSERT_FIELD_ORDER = [
  '_type',
  'documentId',
  'html',
  'id',
  'position',
  'reasoning',
] satisfies (keyof InsertSuggestion)[];

const REMOVE_FIELD_DESCRIPTIONS = {
  _type: `\`"${removeType}"\``,
  documentId: 'The id of the document the content is removed from',
  ids: 'The ids of EVERY paragraph to remove, each copied **exactly** from the `id` attribute of its `<p>` element in the document context. Never invent or alter these values. There is no implicit range between ids — a paragraph is removed only if its id is listed.',
  reasoning: 'Why this content is being removed — shown to the user alongside the removal',
} satisfies Record<keyof RemoveSuggestion, string>;

const REMOVE_FIELD_ORDER = [
  '_type',
  'documentId',
  'ids',
  'reasoning',
] satisfies (keyof RemoveSuggestion)[];

const REMOVE_ROWS_FIELD_DESCRIPTIONS = {
  _type: `\`"${removeRowsType}"\``,
  documentId: 'The id of the document the rows are removed from',
  ids: 'The ids of EVERY row to remove, each copied **exactly** from the `id` attribute of its `<tr>` element in the document context (row ids start with `row-`). Never invent or alter these values, and never use a paragraph id here. A row is removed only if its id is listed.',
  reasoning: 'Why these rows are being removed — shown to the user alongside the removal',
} satisfies Record<keyof RemoveRowsSuggestion, string>;

const REMOVE_ROWS_FIELD_ORDER = [
  '_type',
  'documentId',
  'ids',
  'reasoning',
] satisfies (keyof RemoveRowsSuggestion)[];

const REMOVE_COLUMNS_FIELD_DESCRIPTIONS = {
  _type: `\`"${removeColumnsType}"\``,
  documentId: 'The id of the document the columns are removed from',
  ids: 'Paragraph ids identifying the columns to remove — ONE id of a paragraph inside ANY cell of each column (the header cell is a natural choice), copied **exactly** from the `id` attribute of its `<p>` element in the document context. A column is removed when a paragraph in any of its cells is listed.',
  reasoning: 'Why these columns are being removed — shown to the user alongside the removal',
} satisfies Record<keyof RemoveColumnsSuggestion, string>;

const REMOVE_COLUMNS_FIELD_ORDER = [
  '_type',
  'documentId',
  'ids',
  'reasoning',
] satisfies (keyof RemoveColumnsSuggestion)[];

const REMOVE_TABLE_FIELD_DESCRIPTIONS = {
  _type: `\`"${removeTableType}"\``,
  documentId: 'The id of the document the table is removed from',
  id: "The table's own id — copied **exactly** from the `id` attribute of the `<table>` element in the document context (table ids start with `table-`). Never invent or alter this value, and never use a paragraph or row id here.",
  reasoning: 'Why the table is being removed — shown to the user alongside the removal',
} satisfies Record<keyof RemoveTableSuggestion, string>;

const REMOVE_TABLE_FIELD_ORDER = [
  '_type',
  'documentId',
  'id',
  'reasoning',
] satisfies (keyof RemoveTableSuggestion)[];

const INSERT_ROWS_FIELD_DESCRIPTIONS = {
  _type: `\`"${insertRowsType}"\``,
  documentId: 'The id of the document the rows are inserted into',
  html: 'One or more `<tr>` elements holding the new rows and their `<td>` cell content. Plain HTML only — do NOT include `<change>` elements or a wrapping `<table>`.',
  id: "The reference row's own id — copied **exactly** from the `id` attribute of the `<tr>` element in the document context (row ids start with `row-`). Never invent or alter this value, and never use a paragraph id here.",
  position: `\`"${abovePosition}"\` or \`"${belowPosition}"\` — whether the new rows go immediately above or below the reference row`,
  reasoning: 'Why these rows are being inserted — shown to the user alongside the insertion',
} satisfies Record<keyof InsertRowsSuggestion, string>;

const INSERT_ROWS_FIELD_ORDER = [
  '_type',
  'documentId',
  'html',
  'id',
  'position',
  'reasoning',
] satisfies (keyof InsertRowsSuggestion)[];

const NOTE_FIELD_DESCRIPTIONS = {
  _type: `\`"${noteType}"\``,
  documentId: 'The id of the document the note is about',
  text: 'The note itself — one or two short sentences, shown to the user as a card',
} satisfies Record<keyof NoteSuggestion, string>;

const NOTE_FIELD_ORDER = ['_type', 'documentId', 'text'] satisfies (keyof NoteSuggestion)[];

/**
 * These examples are serialized verbatim into the prompt, and models mirror
 * their key order — `_type` first, then the rest alphabetical, matching the
 * schemas above. See the key-order comment on the schemas for why `_type`
 * must lead.
 */

const SINGLE_CHANGE_EXAMPLE = {
  suggest: {
    _type: 'edit-paragraph',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: '<p id="aB3xK9m"><change reasoning="The fox is not fast anymore">The slow brown fox jumps over the lazy dog.</change> It had been walking all day and was exhausted. The dog, unbothered, continued to nap in the sun.</p>',
    id: 'aB3xK9m',
  },
} satisfies HtmlEdit;

const SPELLING_FIX_EXAMPLE = {
  suggest: {
    _type: 'edit-paragraph',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: '<p id="aB3xK9m">Each party shall maintain the <change reasoning="Fix typo">confidentiality</change> of the other party\'s information.</p>',
    id: 'aB3xK9m',
  },
} satisfies HtmlEdit;

const MULTIPLE_CHANGES_EXAMPLE = {
  suggest: {
    _type: 'edit-paragraph',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: '<p id="aB3xK9m"><change reasoning="The fox is not fast anymore">The slow brown fox jumps over the lazy dog.</change> It had been walking all day and was exhausted. <change reasoning="The dog is drowsy rather than merely inactive">The sleepy dog, unbothered, continued to nap in the sun.</change></p>',
    id: 'aB3xK9m',
  },
} satisfies HtmlEdit;

const INSERT_EXAMPLE = {
  suggest: {
    _type: 'insert',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: '<p>The obligations in this Section survive termination of this Agreement for a period of three (3) years.</p>',
    id: 'aB3xK9m',
    position: 'after',
    reasoning: 'Add a survival clause so confidentiality obligations outlast termination',
  },
} satisfies HtmlEdit;

const LIST_INSERT_EXAMPLE = {
  suggest: {
    _type: 'insert',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: `<p ${listLevelAttr}="1" ${listTypeAttr}="${numberedListType}">Each party shall bear its own legal costs.</p>`,
    id: 'aB3xK9m',
    position: 'after',
    reasoning: 'Add a costs item to the obligations list',
  },
} satisfies HtmlEdit;

const REMOVE_SINGLE_EXAMPLE = {
  suggest: {
    _type: 'remove',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    ids: ['aB3xK9m'],
    reasoning:
      'The exclusivity clause conflicts with the non-exclusive license granted in Section 2',
  },
} satisfies HtmlEdit;

const REMOVE_SECTION_EXAMPLE = {
  suggest: {
    _type: 'remove',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    ids: ['aB3xK9m', 'cD4yL0n', 'pQ7rT2c'],
    reasoning: 'Section 8 duplicates the indemnification obligations now covered by Section 5',
  },
} satisfies HtmlEdit;

const REMOVE_ROWS_EXAMPLE = {
  suggest: {
    _type: 'remove-rows',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    ids: ['row-tR2kP8wQx4zF', 'row-mN5vB1qYw7jD'],
    reasoning: 'The volume-discount tiers were dropped from the deal',
  },
} satisfies HtmlEdit;

const REMOVE_COLUMNS_EXAMPLE = {
  suggest: {
    _type: 'remove-columns',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    ids: ['tR2kP8w'],
    reasoning: 'The unit-price column is superseded by the flat fee in Section 4',
  },
} satisfies HtmlEdit;

const REMOVE_TABLE_EXAMPLE = {
  suggest: {
    _type: 'remove-table',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    id: 'table-tR2kP8wQx4zF',
    reasoning: 'The pricing schedule was replaced by the flat fee in Section 4',
  },
} satisfies HtmlEdit;

const NOTE_EXAMPLE = {
  suggest: {
    _type: 'note',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    text: 'Clause 7.2 already limits liability to direct damages, so no edit is needed for this request.',
  },
} satisfies HtmlEdit;

const INSERT_ROWS_EXAMPLE = {
  suggest: {
    _type: 'insert-rows',
    documentId: 'd494209a-7c63-4e7c-bdb2-1951e71c1a4d',
    html: '<tr><td>Enterprise</td><td>$40,000 per year</td></tr>',
    id: 'row-tR2kP8wQx4zF',
    position: 'below',
    reasoning: 'Add the Enterprise tier the parties agreed on',
  },
} satisfies HtmlEdit;

function renderFieldDescription<TField extends string>(
  field: TField,
  descriptions: Record<TField, string>
) {
  return `- \`${field}\`: ${descriptions[field]}`;
}

function formatHtmlEditExample(example: HtmlEdit) {
  return JSON.stringify(example, null, 2);
}

export const HTML_OUTPUT_FORMAT = `## ${HTML_OUTPUT_FORMAT_TITLE}

Express every suggestion as a structured output object with a \`${suggestKey}\` key containing a \`${typeKey}\` field and the fields required by that type. **Always emit \`${typeKey}\` as the FIRST field of the suggestion**, then the remaining fields in the order documented for that type. Emit one suggestion per changed paragraph. Do not explain your changes in prose; output only structured suggestions.

## ${editParagraphType}

Use this to replace the content of a paragraph from the input document.

**Fields:**
${EDIT_PARAGRAPH_FIELD_ORDER.map(field => renderFieldDescription(field, EDIT_PARAGRAPH_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- Output only the HTML you want the paragraph to look like after the edit — do not produce diffs, \`<del>\`, or \`<ins>\` elements. Diffing is handled by downstream code.
- Always include the \`id\` attribute on the \`<p>\` element, matching the original paragraph's id.
- **CRITICAL: Every change MUST be wrapped in a \`<change>\` element with a \`reasoning\` attribute. This is not optional — any modified text that is not enclosed in a \`<change>\` element is invalid output.** The attribute value explains why you made that specific change.
- When a paragraph has multiple changes, emit a single \`${editParagraphType}\` suggestion whose \`${htmlKey}\` contains the full final paragraph with each changed region wrapped in its own \`<change>\` element.
- **CRITICAL: "Change" means ANY byte that differs from the original — including capitalization, punctuation, whitespace, and list-item labels.** A common mistake is leaving small implicit edits outside \`<change>\` tags because they "feel" like trivial adjustments. They are still changes and must be wrapped.
- **How much text each \`<change>\` covers — this is the highlighted region the user sees, and the unit they accept or reject:**
  - A purely mechanical fix to a single word (a spelling correction, capitalization, or one punctuation mark) wraps just that word.
  - Everything else wraps AT LEAST the entire sentence containing the change, from its first word through its closing punctuation: any edit that changes meaning, and any sentence where more than one word is modified.
  - Several modifications in ONE sentence share ONE \`<change>\` around the whole sentence, with a reasoning that covers them together. Changes in different sentences get separate \`<change>\` elements, one per sentence.
  - A rewrite that flows across sentence boundaries is one \`<change>\` covering all affected sentences.
  - Wrapping more text does NOT mean changing more text — unchanged words inside the element must stay byte-identical to the original. The wrapper only sets the highlight; the minimal-diff rules still apply to the text itself.
- **Formatting-only changes are not yet supported.** Only text differences are applied — inline formatting in your \`${htmlKey}\` (bold, italics, underline, links, heading conversion) is ignored. Never emit an \`${editParagraphType}\` whose only change is formatting; it would silently do nothing. If the user asks for a formatting-only change, emit a \`${noteType}\` explaining that formatting changes are not supported yet.
<example type="single-change">
The input paragraph in the document context is \`<p id="aB3xK9m">The quick brown fox jumps over the lazy dog. It had been walking all day and was exhausted. The dog, unbothered, continued to nap in the sun.</p>\`. Changing \`quick\` to \`slow\` alters the sentence's meaning, so the \`<change>\` wraps the whole sentence even though only one word differs:

${formatHtmlEditExample(SINGLE_CHANGE_EXAMPLE)}
</example>

<example type="spelling-fix-single-word">
The input paragraph contains the misspelling \`confidentialty\`. A mechanical single-word fix, so the \`<change>\` wraps only the corrected word:

${formatHtmlEditExample(SPELLING_FIX_EXAMPLE)}
</example>

<example type="multiple-changes-in-same-paragraph">
Two edits to the same paragraph — changing \`quick\` to \`slow\` and adding \`sleepy\`. The changes are in different sentences, so each gets its own \`<change>\` wrapping its whole sentence:

${formatHtmlEditExample(MULTIPLE_CHANGES_EXAMPLE)}
</example>

## ${insertType}

Use this to insert new content into the document, positioned relative to an existing paragraph. Do NOT use \`${editParagraphType}\` to append new paragraphs onto an existing one — use \`${insertType}\` instead.

**Fields:**
${INSERT_FIELD_ORDER.map(field => renderFieldDescription(field, INSERT_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- Unlike \`${editParagraphType}\`, do NOT use \`<change>\` elements in the \`${htmlKey}\`. The entire insertion is the change; put the explanation in the \`${reasoningKey}\` field instead.
- The \`${idKey}\` must reference an existing paragraph in the input document; the inserted content does not get an id of its own.
- The \`${htmlKey}\` may only use the supported HTML features listed below. Unsupported elements and styles are stripped or ignored.

**Supported HTML:**
- Block elements: \`<p>\`, headings \`<h1>\`–\`<h6>\` (\`<h3>\`–\`<h6>\` all render as heading level 3), \`<blockquote>\` (rendered as indentation), list items (see "Lists" below), \`<table>\` / \`<tr>\` / \`<td>\` / \`<th>\` (with \`colspan\` / \`rowspan\`), \`<pre>\` (monospace; each line becomes a paragraph), and \`<br>\`.
- Inline elements: \`<b>\` / \`<strong>\`, \`<i>\` / \`<em>\`, \`<u>\`, \`<s>\` / \`<del>\`, \`<span>\`, and \`<a href="...">\`.
- Inline \`style\` on text and inline elements: \`color\`, \`font-size\` (\`pt\` or \`px\`), \`font-weight\`, \`font-family\`, \`font-style\`, \`text-decoration: underline | line-through\`, \`vertical-align: super | sub\`.
- Inline \`style\` on block elements: \`text-align: left | center | right | justify\`, \`margin-top\` / \`margin-bottom\` (\`pt\` or \`px\`), \`background-color\`.

**Lists:**
- **Adding an item to a list whose items are each their own \`<p>\` (they show \`${listLevelAttr}\` / \`${listTypeAttr}\` in the document context) is an \`${insertType}\` of a new list-item paragraph — never an \`${editParagraphType}\` of a neighboring item.**
- Write each list item as a \`<p>\` paragraph carrying \`${listLevelAttr}\` and \`${listTypeAttr}\` — the same attributes list items use in the input document context. Do NOT use \`<ul>\` / \`<ol>\` / \`<li>\`.
  - \`${listLevelAttr}\` is 1-based: \`1\` = top level, \`2\` = nested under the level above it, and so on.
  - \`${listTypeAttr}\` is \`${numberedListType}\` or \`${bulletListType}\`. **Default to \`${numberedListType}\`** — these are legal documents, where numbered lists are the norm. Use \`${bulletListType}\` only when the user explicitly asks for bullets or you are continuing an existing bullet list.
- To CONTINUE an existing list, set the insert's \`${idKey}\` + \`${positionKey}\` to one of its items and give your new items the same \`${listLevelAttr}\` / \`${listTypeAttr}\`. They join that list and Aline renumbers it automatically.
- NEVER write the list number or marker into the content (e.g. \`1.\`, \`(a)\`, \`•\`). The \`${listMarkerAttr}\` shown in the input is display-only; Aline renders the marker from \`${listLevelAttr}\` / \`${listTypeAttr}\`.

<example type="insert">
Inserting a new paragraph immediately after the paragraph with \`id\` = \`"aB3xK9m"\`:

${formatHtmlEditExample(INSERT_EXAMPLE)}
</example>

<example type="insert-list-item">
Continuing the numbered list whose last item has \`id\` = \`"aB3xK9m"\` — the new item joins that list and is renumbered automatically:

${formatHtmlEditExample(LIST_INSERT_EXAMPLE)}
</example>

## ${removeType}

Use this to delete whole paragraphs from the document. List the id of every paragraph to remove; the removal is applied as suggested deletions the user can accept or reject.

**Fields:**
${REMOVE_FIELD_ORDER.map(field => renderFieldDescription(field, REMOVE_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- List EVERY paragraph to delete by id — headings and list items included. To delete a whole section, list each of its paragraphs; do not list just the first and last.
- Listed paragraphs that are adjacent in the document are combined into one removal suggestion; paragraphs elsewhere in the document become their own suggestions, each accepted or rejected independently.
- A table that sits between two listed adjacent paragraphs is removed with them — so listing all of a section's paragraphs also removes the section's tables.
- Listing a paragraph that is inside a table cell clears that cell's content but keeps the cell. To delete table rows use \`${removeRowsType}\`, table columns \`${removeColumnsType}\`, and a whole table \`${removeTableType}\`.
- Use \`${removeType}\` — never an \`${editParagraphType}\` with emptied html — when a paragraph should be gone entirely.
- Do NOT pair a \`${removeType}\` with an \`${insertType}\` to make small wording changes to a paragraph; that is an \`${editParagraphType}\`. Use \`${removeType}\` + \`${insertType}\` only when genuinely replacing whole paragraphs with different content.

<example type="remove-single-paragraph">
Removing the single paragraph with \`id\` = \`"aB3xK9m"\`:

${formatHtmlEditExample(REMOVE_SINGLE_EXAMPLE)}
</example>

<example type="remove-section">
Removing a three-paragraph section (any table between the listed adjacent paragraphs is removed with them):

${formatHtmlEditExample(REMOVE_SECTION_EXAMPLE)}
</example>

## ${removeRowsType}

Use this to delete individual rows from a table. The rows are struck through as suggested deletions the user can accept or reject; accepting the removal of a table's last remaining rows removes the table itself. To delete a whole table, use \`${removeTableType}\` instead of listing every row.

**Fields:**
${REMOVE_ROWS_FIELD_ORDER.map(field => renderFieldDescription(field, REMOVE_ROWS_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- Identify each row by its own \`row-…\` id from the \`<tr>\` element — never by a paragraph id inside it.
- Adjacent listed rows are combined into one removal suggestion; non-adjacent rows (or rows in different tables) become separate suggestions, each accepted or rejected independently.
- Use \`${removeRowsType}\` only for table structure. Deleting non-table paragraphs is a \`${removeType}\`; deleting columns is a \`${removeColumnsType}\`; changing text inside a cell is an \`${editParagraphType}\`.

<example type="remove-rows">
Removing two adjacent table rows, each identified by its \`<tr>\` id:

${formatHtmlEditExample(REMOVE_ROWS_EXAMPLE)}
</example>

## ${removeColumnsType}

Use this to delete individual columns from a table. Every cell of the column is struck through as a suggested deletion the user can accept or reject.

**Fields:**
${REMOVE_COLUMNS_FIELD_ORDER.map(field => renderFieldDescription(field, REMOVE_COLUMNS_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- Identify each column by ONE paragraph id from any of its cells; one id per column is enough. Every listed id must be inside a table cell.
- Adjacent listed columns are combined into one removal suggestion; non-adjacent columns (or columns in different tables) become separate suggestions, each accepted or rejected independently.
- Do NOT list every column of a table — that is a \`${removeTableType}\`. A ${removeColumnsType} covering all columns is rejected.
- A horizontally merged cell spans several columns; listing a paragraph inside one removes the cell's whole span.
- Use \`${removeColumnsType}\` only for table structure. Deleting rows is a \`${removeRowsType}\`; changing text inside a cell is an \`${editParagraphType}\`.

<example type="remove-columns">
Removing the column that contains the paragraph with \`id\` = \`"tR2kP8w"\` (here, a header cell of that column):

${formatHtmlEditExample(REMOVE_COLUMNS_EXAMPLE)}
</example>

## ${removeTableType}

Use this to delete an entire table.

**Fields:**
${REMOVE_TABLE_FIELD_ORDER.map(field => renderFieldDescription(field, REMOVE_TABLE_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- The \`${idKey}\` is the table's own \`table-…\` id from the \`<table>\` element — do NOT use a paragraph or row id, and do NOT enumerate its rows or cells.
- Use \`${removeTableType}\` when the table should be gone entirely; individual rows are a \`${removeRowsType}\`; individual columns are a \`${removeColumnsType}\`; the paragraphs around a table are a \`${removeType}\`.

<example type="remove-table">
Removing the entire table whose \`<table>\` element has \`id\` = \`"table-tR2kP8wQx4zF"\`:

${formatHtmlEditExample(REMOVE_TABLE_EXAMPLE)}
</example>

## ${insertRowsType}

Use this to insert new rows into an existing table, positioned relative to a row it already has.

**Fields:**
${INSERT_ROWS_FIELD_ORDER.map(field => renderFieldDescription(field, INSERT_ROWS_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- The \`${htmlKey}\` is \`<tr>\` elements only — no wrapping \`<table>\`, no \`<change>\` elements; the whole insertion is one change and the explanation goes in \`${reasoningKey}\`.
- Give each row at most as many \`<td>\` cells as the table has columns; rows with fewer cells are padded with empty cells. Column widths come from the table — do not set widths.
- Cell content supports the same inline HTML as \`${insertType}\` (formatting, multiple \`<p>\` paragraphs per cell).
- The \`${idKey}\` is the reference row's own \`row-…\` id from its \`<tr>\` element — never a paragraph id inside it.
- Use \`${insertRowsType}\` only for adding rows to an existing table. A brand-new table is an \`${insertType}\`; changing text in an existing cell is an \`${editParagraphType}\`.

<example type="insert-rows">
Inserting one row below the row whose \`<tr>\` element has \`id\` = \`"row-tR2kP8wQx4zF"\`:

${formatHtmlEditExample(INSERT_ROWS_EXAMPLE)}
</example>

## ${noteType}

Use this to tell the user, briefly, why you are NOT making an edit they asked for. It renders as a card alongside your suggestions; nothing is applied to the document. This is your only channel for user-facing text — anything you write outside structured suggestions is discarded.

**Fields:**
${NOTE_FIELD_ORDER.map(field => renderFieldDescription(field, NOTE_FIELD_DESCRIPTIONS)).join('\n')}

**Rules:**
- Emit a note ONLY for a request (or part of one) you are not fulfilling: the change is unsupported (e.g. formatting-only), the target text cannot be found, or no edit is needed because the document already satisfies the request.
- NEVER use a note to narrate, summarize, or justify edits you DID emit — their cards already carry your reasoning.
- NEVER use a note to ask the user a question; you cannot receive an answer.
- Keep it to one or two sentences, in the user's language.

<example type="note">
Declining to edit because the document already does what the user asked:

${formatHtmlEditExample(NOTE_EXAMPLE)}
</example>
`;
